import { ActionItem, SEV } from './types';
import { DEPT_DB_MAP, formatAmount, titleCase } from '../../constants';
import KpiGrid from '../../components/ui/KpiGrid';
import TabBar from '../../components/ui/TabBar';
import FilterBar from '../../components/ui/FilterBar';
import ActionButton from '../../components/ui/ActionButton';

interface InboxTableProps {
  filteredItems: ActionItem[];
  selected: ActionItem | null;
  inboxTotal: number;
  inboxPage: number;
  pageSize: number;
  filter: 'all' | 'PENDING' | 'IN_PROGRESS';
  items: ActionItem[];
  selectedDept: string;
  deptCounts: Record<string, number>;
  deptTotal: number;
  stats: { pending: number; in_review: number; completed: number; overdue: number; sla_compliance: number; total: number } | null;
  onSelectItem: (item: ActionItem) => void;
  onSetFilter: (f: 'all' | 'PENDING' | 'IN_PROGRESS') => void;
  onSetSelectedDept: (dept: string) => void;
  onSetInboxPage: (page: number) => void;
}

export default function InboxTable({
  filteredItems, selected, inboxTotal, inboxPage, pageSize,
  filter, items, selectedDept, deptCounts, deptTotal, stats,
  onSelectItem, onSetFilter, onSetSelectedDept, onSetInboxPage,
}: InboxTableProps) {
  return (
    <>
      {/* Department filter */}
      <FilterBar filters={[
        {
          key: 'dept',
          label: 'Department',
          type: 'select',
          value: selectedDept,
          onChange: (v) => { onSetSelectedDept(v); onSetInboxPage(0); },
          options: Object.entries(deptCounts)
            .sort((a, b) => b[1] - a[1])
            .map(([dept, count]) => ({
              value: dept,
              label: `${titleCase(DEPT_DB_MAP[dept] || dept)} (${count})`,
            })),
          placeholder: `All Departments (${deptTotal})`,
        },
      ]} />

      {/* Stats row */}
      {stats && (
        <KpiGrid
          columns={5}
          items={[
            { label: 'Pending', value: stats.pending, color: 'var(--warning)' },
            { label: 'In Review', value: stats.in_review, color: 'var(--navy-700)' },
            { label: 'Completed', value: stats.completed, color: 'var(--positive)' },
            { label: 'Overdue', value: stats.overdue, color: '#ef4444' },
            { label: 'SLA Compliance', value: `${stats.sla_compliance}%`, color: stats.sla_compliance >= 90 ? 'var(--positive)' : '#f59e0b' },
          ]}
        />
      )}

      {/* Filter tabs */}
      <TabBar
        tabs={[
          { key: 'all', label: 'All', count: items.length },
          { key: 'PENDING', label: 'Pending', count: items.filter(i => i.status === 'PENDING').length },
          { key: 'IN_PROGRESS', label: 'In Progress', count: items.filter(i => i.status === 'IN_PROGRESS').length },
        ]}
        active={filter}
        onChange={(k) => onSetFilter(k as any)}
      />

      {/* Item list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {filteredItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
            {inboxTotal === 0 ? 'No items in your inbox' : 'No items match current filter'}
          </div>
        ) : (
          filteredItems.map(item => {
            const sev = SEV[item.exception?.severity] || SEV.medium;
            const isSelected = selected?.id === item.id;
            return (
              <div key={item.id} onClick={() => onSelectItem(item)} style={{
                padding: '12px 16px', borderRadius: 8, cursor: 'pointer',
                background: isSelected ? 'var(--accent-subtle)' : 'var(--bg-surface)',
                border: isSelected ? '1px solid var(--accent-border)' : '1px solid var(--border-subtle)',
                display: 'flex', alignItems: 'center', gap: 12, transition: 'all 0.15s',
              }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: sev.color, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>{item.exception?.ref}</span>
                    <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: sev.bg, color: sev.color, fontWeight: 600, textTransform: 'uppercase' }}>
                      {item.exception?.severity}
                    </span>
                    <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: '#f5f3ff', color: 'var(--navy-800)', fontWeight: 600 }}>
                      {item.current_level}
                    </span>
                    <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: item.cascade_authority === 'REVIEW' ? 'var(--accent-subtle)' : '#f0fdf4', color: item.cascade_authority === 'REVIEW' ? 'var(--navy-600)' : 'var(--positive)', fontWeight: 600 }}>
                      {item.cascade_authority === 'REVIEW' ? 'REVIEW' : 'APPROVE'}
                    </span>
                    {item.is_overdue && (
                      <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 3, background: '#fef2f2', color: '#ef4444', fontWeight: 700 }}>OVERDUE</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.action_label}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>
                    {titleCase(item.exception?.type)} | {item.exception?.entity || '?'} | {titleCase(DEPT_DB_MAP[item.current_department] || item.current_department)}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
                    {formatAmount(item.exception?.amount || 0)}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{item.status}</div>
                </div>
              </div>
            );
          })
        )}
        {/* Pagination */}
        {inboxTotal > pageSize && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 8 }}>
            <ActionButton
              label="PREV"
              onClick={() => onSetInboxPage(Math.max(0, inboxPage - 1))}
              variant="secondary"
              size="sm"
              disabled={inboxPage === 0}
            />
            <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>
              {inboxPage * pageSize + 1}-{Math.min((inboxPage + 1) * pageSize, inboxTotal)} of {inboxTotal}
            </span>
            <ActionButton
              label="NEXT"
              onClick={() => onSetInboxPage(inboxPage + 1)}
              variant="secondary"
              size="sm"
              disabled={(inboxPage + 1) * pageSize >= inboxTotal}
            />
          </div>
        )}
      </div>
    </>
  );
}
